import React from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Apple,
  CheckCircle,
  ArrowRight,
  Clock,
  Utensils,
  Target,
  MessageCircle,
  Calendar
} from 'lucide-react'
import Seo from '../components/Seo'
import { coachingPricing } from '../lib/coachingPrograms'
import { EnrollLink } from '../components/ui/EnrollLink'

const MealPlan: React.FC = () => {
  const nutritionProgram = {
    name: 'Nutrition Blueprint',
    pricing: coachingPricing.nutritionBlueprint[0],
    description:
      'Personalized nutrition coaching for women who want more structure, clarity, and consistency with food—whether they’re just getting started, getting back into a routine, or looking to improve the habits they already have.',
    features: [
      'Initial nutrition and lifestyle assessment',
      'Personalized nutrition strategy',
      'Calorie and protein recommendations when appropriate',
      'Meal-building guidance',
      'Three private coaching consultations',
      'Scheduled progress reviews and nutrition adjustments',
      'Strategies for weekends, dining out, travel, and social events',
      'Final transition and maintenance plan'
    ]
  }

  const coachingPillars = [
    {
      icon: Target,
      title: 'Personalized Strategy',
      description:
        'Your nutrition strategy is built around your goals, preferences, routine, and starting point — not a generic meal plan.'
    },
    {
      icon: Utensils,
      title: 'Real-Life Nutrition',
      description:
        'Learn how to build meals and make decisions that work at home, at restaurants, while traveling, and during busy weeks.'
    },
    {
      icon: MessageCircle,
      title: 'Private Coaching',
      description:
        'Three private consultations plus scheduled progress reviews give you guidance, accountability, and adjustments along the way.'
    },
    {
      icon: Apple,
      title: 'Sustainable Habits',
      description:
        "Build nutrition habits that work beyond the 12 weeks. The goal isn't to follow a perfect plan forever—it's to feel confident making choices that work for you."
    }
  ]

  const sampleMeals = [
    {
      name: '5 Minute Spicy Tuna Rolls',
      image: '/images/recipe-tuna-rolls.webp',
      alt: 'Spicy tuna rolls wrapped in cucumber with avocado and sriracha mayo',
      time: '5 min',
      calories: 396,
      protein: 12,
      carbs: 22,
      fat: 31,
      ingredients: [
        '1 cucumber',
        '1 can or pouch of tuna',
        '1 Tbsp mayo (for tuna mixture)',
        '2 tsp sriracha',
        '2 tsp garlic powder',
        'Salt & pepper to taste',
        'Avocado, sliced',
        'Sauce: 2 Tbsp mayo + 2 tsp sriracha'
      ]
    },
    {
      name: 'Avocado Egg Salad Sandwiches',
      image: '/images/recipe-egg-salad.webp',
      alt: 'Avocado egg salad on toasted whole-wheat bread with fresh chives',
      time: '15 min',
      calories: 350,
      protein: 17,
      carbs: 30,
      fat: 19,
      ingredients: [
        '½ ripe avocado',
        '1½ tsp lemon juice',
        '1 tsp avocado oil',
        '3 hard-boiled eggs, chopped',
        '¼ cup finely chopped celery',
        '1 Tbsp snipped fresh chives',
        '¼ tsp salt, ⅛ tsp pepper',
        '4 slices whole-wheat bread, toasted',
        '2 leaves lettuce'
      ]
    },
    {
      name: 'Grilled Blackened Shrimp Tacos',
      image: '/images/recipe-shrimp-tacos.webp',
      alt: 'Grilled blackened shrimp tacos with avocado crema, pico de gallo and lime',
      time: '20 min',
      calories: 286,
      protein: 24,
      carbs: 30,
      fat: 9,
      ingredients: [
        '1 ripe avocado',
        '1 Tbsp lime juice',
        '1 small clove garlic, grated',
        '¼ tsp salt',
        '1 lb large raw shrimp, peeled & deveined',
        '2 Tbsp salt-free Cajun spice blend',
        '8 corn tortillas, warmed',
        '2 cups iceberg lettuce, chopped',
        '½ cup fresh cilantro',
        '½ cup prepared pico de gallo'
      ]
    }
  ]

  return (
    <main className="min-h-screen bg-brand-cream text-brand-navy">
      <Seo
        title="Nutrition Blueprint — Fit by Cassie"
        description="12 weeks of personalized nutrition coaching built around your goals, preferences, and real life. Founding client investment: $349."
        image="/images/meal-prep-hero.webp"
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-brand-cream px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
        <div
          className="absolute -top-40 -left-32 w-[520px] h-[520px] rounded-full bg-brand-teal/10 blur-3xl pointer-events-none"
          aria-hidden
        />
        <div
          className="absolute -bottom-40 right-0 w-[420px] h-[420px] rounded-full bg-brand-blush/15 blur-3xl pointer-events-none"
          aria-hidden
        />

        <div className="container-max relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="inline-block text-xs font-bold uppercase tracking-widest-xl text-brand-teal mb-4">
                12-Week Nutrition Coaching
              </span>

              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] text-brand-navy mb-6">
                Nutrition{' '}
                <em className="block italic font-normal text-brand-teal mt-1">
                  Blueprint
                </em>
              </h1>

              <p className="font-display text-xl sm:text-2xl text-brand-navy/85 leading-relaxed mb-5">
                Get your nutrition together without putting your life on hold.
              </p>

              <p className="text-base sm:text-lg text-brand-navy/70 leading-relaxed max-w-xl mb-8">
                Personalized nutrition coaching for women who want more
                structure, clarity, and consistency with food—whether
                they're just getting started, getting back into a routine,
                or looking to improve the habits they already have.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-8">
                <Link
                  to="/book-consultation"
                  className="inline-flex items-center justify-center rounded-full bg-brand-navy text-brand-cream font-semibold px-8 py-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-soft-lg"
                >
                  Schedule Your Complimentary Consultation
                  <Calendar className="ml-2" size={18} />
                </Link>

                <a
                  href="#investment"
                  className="inline-flex items-center justify-center rounded-full border border-brand-navy/25 bg-brand-ivory text-brand-navy font-semibold px-8 py-4 transition-colors hover:bg-white"
                >
                  View Founding Investment
                </a>
              </div>

              <div className="flex flex-wrap gap-x-6 gap-y-3">
                {[
                  'Personalized',
                  'Real Food',
                  'Accountability',
                  'Sustainable'
                ].map((item) => (
                  <span
                    key={item}
                    className="flex items-center gap-2 text-sm font-semibold text-brand-navy/70"
                  >
                    <CheckCircle size={15} className="text-brand-teal" />
                    {item}
                  </span>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="relative"
            >
              <div className="absolute -inset-4 rounded-[2rem] border border-brand-bronze/20" />
              <div className="relative rounded-3xl overflow-hidden shadow-soft-lg bg-brand-ivory">
                <img
                  src="/images/meal-prep-hero.webp"
                  alt="Cassie preparing healthy food in a bright kitchen"
                  className="w-full h-full object-cover object-[center_28%] aspect-[4/5] sm:aspect-[3/4]"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* COACHING PILLARS */}
      <section className="bg-brand-ivory px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto text-center mb-14"
          >
            <span className="inline-block text-xs font-bold uppercase tracking-widest-xl text-brand-teal mb-4">
              Nutrition for Real Life
            </span>

            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-brand-navy leading-tight mb-6">
              Structure without{' '}
              <em className="italic font-normal text-brand-teal">
                another restrictive diet.
              </em>
            </h2>

            <p className="text-lg text-brand-navy/70 leading-relaxed">
              The Nutrition Blueprint gives you a personalized framework for
              eating in a way that supports your goals while still fitting the
              life you actually live.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {coachingPillars.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.08 }}
                className="bg-brand-cream rounded-3xl p-7 border border-brand-bronze/15 shadow-soft"
              >
                <div className="w-11 h-11 rounded-full bg-brand-teal/10 flex items-center justify-center mb-5">
                  <item.icon size={21} className="text-brand-teal" />
                </div>

                <h3 className="font-display text-xl font-semibold text-brand-navy mb-3">
                  {item.title}
                </h3>

                <p className="text-sm text-brand-navy/65 leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* INVESTMENT */}
      <section
        id="investment"
        className="bg-brand-cream px-4 sm:px-6 lg:px-8 py-20 sm:py-24"
      >
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <span className="inline-block text-xs font-bold uppercase tracking-widest-xl text-brand-teal mb-4">
                What's Included
              </span>

              <h2 className="font-display text-3xl sm:text-4xl font-semibold text-brand-navy mb-7">
                Twelve weeks of focused{' '}
                <em className="italic font-normal text-brand-teal">
                  nutrition coaching.
                </em>
              </h2>

              <ul className="space-y-4">
                {nutritionProgram.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3">
                    <CheckCircle
                      size={18}
                      className="text-brand-teal shrink-0 mt-0.5"
                    />
                    <span className="text-brand-navy/70 leading-relaxed">
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="bg-brand-ivory rounded-3xl border border-brand-bronze/25 shadow-soft-lg p-8 sm:p-10 lg:sticky lg:top-32"
            >
              <span className="inline-block rounded-full bg-brand-blush/20 px-4 py-2 text-xs font-bold uppercase tracking-widest text-brand-navy mb-6">
                Founding Client Investment
              </span>

              <h3 className="font-display text-3xl font-semibold text-brand-navy mb-2">
                Nutrition Blueprint
              </h3>

              <p className="text-brand-navy/60 mb-8">
                Personalized nutrition coaching
              </p>

              <div className="rounded-2xl bg-brand-cream border border-brand-teal/15 p-6 mb-8">
                <p className="text-xs font-bold uppercase tracking-widest text-brand-teal mb-2">
                  {nutritionProgram.pricing.label}
                </p>

                <p className="font-display text-4xl font-semibold text-brand-navy">
                  {nutritionProgram.pricing.price}
                </p>

                {nutritionProgram.pricing.detail && (
                  <p className="text-sm text-brand-navy/60 mt-2">
                    {nutritionProgram.pricing.detail}
                  </p>
                )}
              </div>

              <p className="text-sm text-brand-navy/65 leading-relaxed mb-7">
                Ready to start? Enroll now, or schedule a complimentary
                consultation first if you'd like to talk through your current
                nutrition, goals, and lifestyle.
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <EnrollLink
                  packageKey="nutritionBlueprint"
                  className="inline-flex flex-1 items-center justify-center rounded-full bg-brand-navy text-brand-cream font-bold px-7 py-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-soft-lg"
                />

                <Link
                  to="/book-consultation"
                  className="inline-flex flex-1 items-center justify-center rounded-full border-2 border-brand-navy text-brand-navy font-bold px-7 py-4 transition-all duration-300 hover:bg-brand-navy hover:text-brand-cream"
                >
                  Schedule a Consultation First
                </Link>
              </div>

              <p className="text-center text-xs text-brand-navy/45 mt-4">
                Enroll any time — a consultation is available but never required.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SAMPLE MEALS */}
      <section className="bg-brand-ivory px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-14"
          >
            <span className="inline-block text-xs font-bold uppercase tracking-widest-xl text-brand-teal mb-4">
              From the Kitchen
            </span>

            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-brand-navy mb-5">
              Real food can still{' '}
              <em className="italic font-normal text-brand-teal">
                support your goals.
              </em>
            </h2>

            <p className="text-lg text-brand-navy/65 max-w-2xl mx-auto leading-relaxed">
              A few examples of everyday meals that can fit into a balanced
              nutrition approach. Your individual needs, portions, and food
              preferences will depend on your goals and your starting point.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-7">
            {sampleMeals.map((meal, index) => (
              <motion.div
                key={meal.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group bg-brand-cream rounded-3xl overflow-hidden shadow-soft border border-brand-bronze/15 flex flex-col"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={meal.image}
                    alt={meal.alt}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />

                  <span className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-ivory/95 backdrop-blur-sm text-xs font-semibold text-brand-navy shadow-soft">
                    <Clock size={12} className="text-brand-teal" />
                    {meal.time}
                  </span>
                </div>

                <div className="p-6 flex flex-col flex-1">
                  <h3 className="font-display text-xl font-semibold text-brand-navy leading-tight mb-4">
                    {meal.name}
                  </h3>

                  <div className="grid grid-cols-4 gap-2 mb-5">
                    {[
                      ['Cal', meal.calories],
                      ['Protein', `${meal.protein}g`],
                      ['Carbs', `${meal.carbs}g`],
                      ['Fat', `${meal.fat}g`]
                    ].map(([label, value]) => (
                      <div
                        key={label}
                        className="bg-brand-ivory border border-brand-teal/10 rounded-xl py-2 px-1 text-center"
                      >
                        <div className="text-sm font-bold text-brand-navy">
                          {value}
                        </div>
                        <div className="text-[10px] text-brand-teal uppercase tracking-wider">
                          {label}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="flex-1">
                    <p className="text-xs font-semibold text-brand-navy/45 uppercase tracking-widest-xl mb-2.5">
                      Ingredients
                    </p>

                    <ul className="space-y-1.5">
                      {meal.ingredients.map((ingredient, i) => (
                        <li
                          key={i}
                          className="flex items-start gap-2 text-sm text-brand-navy/70"
                        >
                          <span className="mt-2 shrink-0 w-1 h-1 rounded-full bg-brand-teal" />
                          {ingredient}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-brand-cream px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
        <div className="container-max text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto"
          >
            <span className="inline-block text-xs font-bold uppercase tracking-widest-xl text-brand-teal mb-4">
              Your Next Step
            </span>

            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-brand-navy leading-tight mb-6">
              Let's make nutrition{' '}
              <em className="italic font-normal text-brand-teal">
                work for your life.
              </em>
            </h2>

            <p className="text-lg text-brand-navy/65 leading-relaxed mb-9 max-w-2xl mx-auto">
              You don't need another set of food rules. You need a plan that
              makes sense for your goals, your preferences, and the life
              you're actually living. Let's talk about where you're starting
              and whether Nutrition Blueprint is the right fit for you.
            </p>

            <Link
              to="/book-consultation"
              className="inline-flex items-center justify-center rounded-full bg-brand-navy text-brand-cream font-bold px-9 py-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-soft-lg"
            >
              Schedule Your Complimentary Consultation
              <ArrowRight className="ml-2" size={20} />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  )
}

export default MealPlan
